// Import 3rd-party dependencies
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';
import moment from 'moment';

// Import project-specific dependencies
import { environment } from '@app/environments/environment';
import { ApiService } from '@app/services/api.service';
import { SEARCHDATA_SERVICE_CONSTS } from '@app/config/constants';

export interface IArticle {
  sysID?: string;
  accessURL?: string;
  articleid?: string;
  articletype?: string;
  kbcategory?: string;
  kbknowledgebase?: string;
  language?: string;
  number?: string;
  previewContent?: string;
  rating?: number;
  shortdescription?: string;
  syscreatedon?: string;
  sysupdatedon?: string;
  sysupdatedby?: string;
  sysviewcount?: number;
  topic?: string;
  ucountry?: string;
  usecount?: number;
  version?: string;
  workflowstate?: string;
  author?: string[];
  updatedurl?: string;
}

export interface IItArticles {
  id?: string;
  contentSource?: string;
  rank?: number;
  sysID?: string;
  accessURL?: string;
  category?: string;
  kbcategory?: string;
  kbknowledgebase?: string;
  number?: string;
  previewContent?: string;
  rating?: number;
  shortdescription?: string;
  syscreatedon?: string;
  sysupdatedby?: string;
  sysupdatedon?: string;
  sysviewcount?: number;
  usecount?: number;
  workflowstate?: string;
  author?: string[];
  updatedurl?: string;
}

@Injectable({
  providedIn: 'root'
})
export class SearchDataService extends ApiService {
  private API_URL = environment.DEPS.SEARCHDATA_API.URL;
  countryList: string[] = [];

  consts = SEARCHDATA_SERVICE_CONSTS;

  constructor(httpClient: HttpClient) {
    super(httpClient);
  }

  addSearchData(searchData: unknown): Observable<unknown> {
    return this.post(`${this.API_URL}/searchdata`, searchData);
  }

  /*------------------------------------------------------------*/
  /*-------------------------HRCONNECT--------------------------*/
  /*------------------------------------------------------------*/

  getTrendingHRArticles(country: string): Observable<IArticle[]> {
    const params = {
      country: this.checkUserCountry(country)
    };
    return this.get<IArticle[]>(`${this.API_URL}/hrconnect`, params).pipe(
      map((item) => {
        return item.map((value) => {
          value.syscreatedon = (moment(value.syscreatedon).format(this.consts.DATE_FORMAT) as unknown) as string;
          value.sysupdatedon = (moment(value.sysupdatedon).format(this.consts.DATE_FORMAT) as unknown) as string;
          value.updatedurl = `${this.consts.HR_ARTICLE_LINK}${value.sysID}`;
          return value;
        });
      }, shareReplay())
    );
  }

  private checkUserCountry(country: string): string {
    return this.countryList.includes(country) ? country : this.consts.GLOBAL;
  }

  getHRConnectCountryList(): Observable<string[]> {
    const params = {
      country: 'global'
    };
    return this.get<IArticle[]>(`${this.API_URL}/hrconnect`, params).pipe(
      map((item) => {
        this.countryList = this.getUniqueCountryList(item);
        return this.countryList;
      }, shareReplay())
    );
  }

  private getUniqueCountryList(list: IArticle[]): string[] {
    const articleList = list;
    const countryList: string[] = [];
    articleList.forEach((hr) => {
      const country = hr.ucountry;
      if (country) {
        if (country.includes(',')) {
          const arr = country.split(',');
          arr.forEach((item) => {
            if (!countryList.includes(item) && !this.consts.COUNTRY_CHECKER_REGEX.test(item.trim())) {
              countryList.push(item.trim());
            }
          });
        } else {
          countryList.push(country);
        }
      }
    });
    const uniqueList = [...new Set(countryList)].sort();

    const republicOfIdx = uniqueList.indexOf(this.consts.LABEL_REPUBLIC_OF);
    if (republicOfIdx !== -1) {
      uniqueList.splice(republicOfIdx, 1);
    }
    return uniqueList;
  }

  /*------------------------------------------------------------*/
  /*-------------------------ITCONNECT--------------------------*/
  /*------------------------------------------------------------*/

  getTrendingITArticles(): Observable<IItArticles[]> {
    return this.get<IItArticles[]>(`${this.API_URL}/itconnect`).pipe(
      map((item) => {
        return item.map((value) => {
          value.syscreatedon = (moment(value.syscreatedon).format(this.consts.DATE_FORMAT) as unknown) as string;
          value.sysupdatedon = (moment(value.sysupdatedon).format(this.consts.DATE_FORMAT) as unknown) as string;
          value.updatedurl = this.consts.ITCONNECT_KB_URL(value.number);
          return value;
        });
      }, shareReplay())
    );
  }
}
